// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs'

const SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN

// 支付网关的脚本 —— 栈里出现任意一帧就无条件保留。
// 要求是「除支付网关以外的三方插件上报全部过滤掉，只留支付」,而 ignoreErrors 只能匹配
// 消息文本、表达不出「除非是支付」,所以有重叠风险的规则一律放到 beforeSend 里,让这份
// 白名单先跑。列表按 .env.local.production 里实际在用的支付 SDK 来:
// Checkout.com Flow(checkout-web-components)/ Barclaycard(Cybersource) Flex Microform / Apple Pay / Google Pay。
const PAYMENT_FRAME = /cdn\.checkout\.com|checkout-web-components\.checkout\.com|flex\.cybersource\.com|applepay\.cdn-apple\.com|pay\.google\.com/

// 三方脚本:整条栈都落在这里面才丢(有一帧是第一方就照常上报)。
// `/analytics\.js` 前面的斜杠是故意的 —— 不加会连我们自己将来的 `xxx-analytics.js` 一起吞掉。
const THIRD_PARTY_FRAME =
  /google-analytics\.com|googletagmanager\.com|doubleclick\.net|\/analytics\.js|webkit-masked-url|chrome-extension|moz-extension|safari(-web)?-extension/

// 第一方代码。components/payment/* 也在这里面 —— 它打包进 /_next/static/chunks/,
// 浏览器里拿到的就是 chunk URL(source map 是 Sentry 服务端才解的,beforeSend 看不到),
// 所以第一方的支付 bug 没法靠上面的域名白名单救,只能靠这条来兜。
const FIRST_PARTY_FRAME = /\/_next\/|^app:\/\/\//

// Sentry SDK 自身的帧。SDK 会包装 XHR / setTimeout / addEventListener 的回调
// (mechanism: instrument),所以三方脚本在回调里抛错时,栈里会多出一帧
// `node_modules/@sentry/browser/esm/helpers.js (sentryWrapped)`。它既不是三方也不是第一方,
// 但足以让下面「整条栈都是三方」的 every() 判断失败 —— RKINGS-4FJ(GA 的 g.readyState,
// 历史 1924 条 / 945 用户)就是这样漏过来的。判断三方时先把这类帧剔掉。
const SDK_FRAME = /node_modules\/@sentry\/|@sentry\/(browser|nextjs|core|utils)/

// 形态上像「代码缺陷」的消息 —— 我们自己写错也会产出一字不差的同一条字符串。
// Flow(和以前的 Frames 一样)是 iframe SDK,输入框全靠跨 iframe postMessage 驱动,BankCards 接线里
// 的 null 引用就长这样。所以只有在栈里完全没有第一方帧(即真的来自原生 bridge / 注入脚本)
// 时才丢。原先这条在 ignoreErrors 里,等于把支付 bug 一起吞了。
const BRIDGE_NOISE = [/Cannot read properties of null \(reading 'postMessage'\)/]

// in-app 浏览器往页面里注入脚本、引用自家原生 bridge 全局时抛的 ReferenceError。
// 各家 App 的名字都不一样(SCDynimacBridge = Snapchat、_AutofillCallbackHandler = iOS 自动填充、
// nativeFunctions = 我们自己的壳、webkit.messageHandlers = WKWebView),逐个往 ignoreErrors 加名字
// 追不完,所以按形态判断。注入脚本的栈会指向文档本身(例如 `/cart:1 (global code)`),既不匹配
// 三方域名也不匹配第一方 chunk,所以只能靠「消息形态 + 栈里没有我们打包出来的第一方帧」来认。
// 注意只匹配「变量不存在」,不碰 `is not a function` —— 那类是我们自己的 bug(如 RKINGS-3TT)。
const INJECTED_GLOBAL_NOISE = [/Can't find variable: /, /\bis not defined\b/]

// 三方脚本(主要是 GA 的 analytics.js)在 XHR 的 onreadystatechange 回调里读了 null 的 readyState。
// 两种浏览器措辞不同:WebKit 是 `null is not an object (evaluating 'g.readyState')`(RKINGS-4FJ),
// Chromium 是 `Cannot read properties of null (reading 'readyState')`(RKINGS-505)。
// Chromium 下跨域脚本没带 CORS,事件**完全没有栈**,所以帧规则救不了(SDK_FRAME 那条要求至少有一帧
// 非 SDK 帧),只能按消息认;仍然要求栈里没有第一方帧 —— 我们自己的代码出这个错会带 /_next/ 帧。
const XHR_READYSTATE_NOISE = [/Cannot read properties of null \(reading 'readyState'\)/, /null is not an object \(evaluating '\w+\.readyState'\)/]

// 网络 / 跨域不透明噪音 —— 带不带第一方帧都是噪音(弱网、被拦截插件挡掉、跨域脚本抛错)。
// 支付相关的**大多**已经在第 1 步被 PAYMENT_FRAME 放行,但有一个例外,见下面第一条:
// - Script error.: 跨域脚本抛错时浏览器给的不透明消息(Flow 的 CDN 不返回 ACAO,不能加 crossOrigin,
//   所以它抛的异常只能是这一条)。
//   ⚠️ 这一条**救不回来**:跨域的 "Script error." 连一帧栈都没有,而第 1 步是按栈帧匹配的,
//   所以 PAYMENT_FRAME 里有没有 Flow 的域名都一样,它必然走到这里被丢掉。
//   Flow 的故障因此不能指望这份配置 —— 改为在 BankCards 的 onError / catch 里用
//   appUtils.reportError 主动上报(第一方调用点,拿得到结构化的 CheckoutError)。
//   别因为「支付已经在白名单里」就把那几处上报删掉,那里是唯一的来源。
// - Failed to fetch / Load failed: Flow SDK 加载失败或 tokenization 请求失败
// - blocked by client: 拦截插件干掉了支付 SDK —— 不是我们的 bug,但是真实的支付故障
const NETWORK_NOISE = [/^Script error\.?$/, /TypeError: (Failed to fetch|Load failed)/, /blocked by client/]

// 取一帧上所有可用于识别来源的字符串。只看 filename 是不够的:Safari 对被遮蔽的脚本
// 给出的 filename 只是 `//hidden/`,`@webkit-masked-url:` 出现在 abs_path 和函数名里
// (RKINGS-3BM `Message Timeout`,历史 708 条 / 79 用户,就是这样漏过三方过滤的)。
const frameSource = f => `${f.filename || ''} ${f.abs_path || ''} ${f.function || ''}`

Sentry.init({
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.NODE_ENV,
  dsn: SENTRY_DSN,
  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 1.0,
  // Note: if you want to override the automatic release value, do not set a
  // `release` value here - use the environment variable `SENTRY_RELEASE`, so
  // that it will also get attached to your source maps

  // 三方噪音的过滤统一放在这里,而不是 ignoreErrors。
  //
  // 原因是执行顺序:ignoreErrors 由 InboundFilters 这个 event processor 实现,跑在
  // BaseClient._prepareEvent() 里面,而 beforeSend 是 _prepareEvent().then(...) 之后才调的
  // (@sentry/core 6.19.7, baseclient.js:445-455)。也就是说 ignoreErrors 永远赢过 beforeSend,
  // 被它丢掉的事件这里根本看不到 —— 所以「保留支付」的判断必须写在 ignoreErrors 之外,
  // 只有 beforeSend 能看到栈帧、分得清支付和其它三方。
  beforeSend(event, hint) {
    const error = hint?.originalException
    if (error?.isAxiosError && !error?.response) {
      return null
    }

    const frames = event.exception?.values?.flatMap(v => v.stacktrace?.frames || []) || []

    // 1) 支付网关的栈无条件保留,必须在所有噪音规则之前
    if (frames.some(f => PAYMENT_FRAME.test(frameSource(f)))) {
      return event
    }

    // 2) 整条栈都在三方脚本里 → 丢;有一帧是第一方就照常上报。
    //    先剔掉 SDK 自己的包装帧(见 SDK_FRAME),否则插桩捕获的三方错误永远过不了 every() 判断。
    //    appFrames.length > 0 是为了不误伤没有栈、或只剩 SDK 帧的事件
    const appFrames = frames.filter(f => !SDK_FRAME.test(frameSource(f)))
    if (appFrames.length > 0 && appFrames.every(f => THIRD_PARTY_FRAME.test(frameSource(f)))) {
      return null
    }

    // 3) 确认与支付无关后,才应用那些有重叠风险的消息规则。
    //    两种形态都比对一遍,对齐 ignoreErrors 自己的匹配方式(value 和 "Type: value")
    const exception = event.exception?.values?.[0]
    const messages = exception ? [exception.value || '', `${exception.type}: ${exception.value}`] : [event.message || '']
    const matches = patterns => patterns.some(re => messages.some(m => re.test(m)))

    // 3a) 像代码缺陷的消息:栈里有第一方帧就当我们自己的 bug 上报,没有才当注入脚本的噪音丢。
    //     INJECTED_GLOBAL_NOISE 额外要求是 ReferenceError,避免误伤同样「找不到东西」但确实是
    //     我们自己写错的 TypeError
    if (!frames.some(f => FIRST_PARTY_FRAME.test(frameSource(f)))) {
      if (matches(BRIDGE_NOISE)) {
        return null
      }
      if (exception?.type === 'ReferenceError' && matches(INJECTED_GLOBAL_NOISE)) {
        return null
      }
      if (matches(XHR_READYSTATE_NOISE)) {
        return null
      }
    }

    // 3b) 网络 / 跨域不透明噪音
    if (matches(NETWORK_NOISE)) {
      return null
    }

    return event
  },

  ignoreErrors: [
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    "Can't find variable: PaymentAutofillConfig",

    // Native app WebView bridge errors - message varies by the native method name
    // (e.g. postMessage, enableDidUserTypeOnKeyboardLogging), so match the family
    /nativeFunctions\.updateBadge is not a function/,
    /nativeFunctions/,

    // Network errors - unstable mobile connections (RKINGS-9)
    /^Network Error$/,

    // Request aborted - user navigated away mid-request (RKINGS-F)
    /^Request aborted$/,

    // Timeout errors - slow connections (RKINGS-Y)
    /timeout of \d+ms exceeded/,

    // Axios cancellation
    /^Cancel/,

    // NOTE: /TypeError: Failed to fetch/ (RKINGS-46G) 和 /TypeError: Load failed/ (RKINGS-16X)
    // 已移到 beforeSend 的 NETWORK_NOISE —— Checkout Flow SDK 加载失败、Checkout tokenization
    // 请求失败都是这两条消息,不能在支付白名单之前丢掉

    // WebKit iOS messageHandlers errors (third-party injections)
    /undefined is not an object.*webkit\.messageHandlers/,
    /webkit\.messageHandlers\[.*\]\.postMessage/,

    // Native app in-app-browser bridge errors (RKINGS-243, RKINGS-2CX, RKINGS-20Q,
    // RKINGS-4J7, RKINGS-4J6, RKINGS-4ZQ, RKINGS-4JF, RKINGS-37Z, RKINGS-4ZV, RKINGS-4B1) -
    // the native method name in the message varies (postMessage, enableDidUserTypeOnKeyboardLogging,
    // etc.), so match the family rather than one exact method name
    // NOTE: /Cannot read properties of null \(reading 'postMessage'\)/ 已移到 beforeSend 的
    // BRIDGE_NOISE —— 这条消息不是厂商专属字符串,Checkout Frames 的 iframe 通信和我们
    // 自己 BankCards 的接线都会产出它,放在 ignoreErrors 里会连支付 bug 一起吞掉。
    // 下面留下的三条都是支付代码产不出来的原生 bridge 字符串,继续走 ignoreErrors。
    /_AutofillCallbackHandler/,
    /Error invoking \w+: Java (object is gone|exception was raised during method invocation)/,
    /WKWebView API client did not respond to this postMessage/,

    // Browser extension / injected script errors (RKINGS-2BJ, RKINGS-4EM, RKINGS-4EK)
    /^SyntaxError: Invalid character: '#'$/,
    /(Range out of order|invalid range) in character class/,

    // Browser extension messaging errors - well-known Chrome/WebExtension API strings,
    // not producible by our own code (RKINGS-3CM, RKINGS-504)
    /Invalid call to runtime\.sendMessage\(\)/,
    /Could not establish connection\. Receiving end does not exist\./,

    // Native fetch() network failure text (Firefox), same class as the axios Network
    // Error already filtered above, just a different call site/wording (RKINGS-1KM)
    /^NetworkError when attempting to fetch resource\.?$/,

    // Unrelated third-party framework error surfacing on our pages (RKINGS-4DX)
    /feature named `pageContext` was not found/,

    // react-slick asNavFor null-check bug when a linked slider unmounts mid-transition
    // (RKINGS-38Y, RKINGS-394, RKINGS-3BE, RKINGS-3BC) - fires inside the library's own
    // event callback, doesn't break rendering, tracked upstream in react-slick
    /Cannot read properties of null \(reading 'slideHandler'\)/,
    /null is not an object \(evaluating 'o\.innerSlider\.slideHandler'\)/,

    // Next.js router's own safety invariant when it detects a hard-navigate to the exact
    // current URL (RKINGS-47G, RKINGS-47W, RKINGS-4K3) - entirely inside next/dist router
    // internals, effectively a no-op since the page is already at that URL
    /Invariant: attempted to hard navigate to the same URL/,

    // Next.js router href/as mismatch invariant (RKINGS-2FB, RKINGS-HQ) - no first-party
    // call site constructs a mismatched href/as pair; root cause is a race between two
    // concurrent router.push calls on the shared router instance (e.g. checkout redirect
    // vs. another nav click), not a fixable single call site
    /The provided `as` value .* is incompatible with the `href` value/,

    // NOTE: /^Script error\.?$/ 已移到 beforeSend 的 NETWORK_NOISE —— 跨域脚本抛错时浏览器
    // 只给这一条不透明消息,Frames 的报错过去就是这样被丢掉的(BankCards 已加 crossOrigin,
    // 让它带上真实消息和栈,这样支付白名单才能生效)

    // Social media SDK errors
    /^Can't find variable: FB/,
    /^Can't find variable: fbq/,
    /^facebook is not defined/,
    /^Can't find variable: tiktok/i,

    // Analytics errors
    /^Can't find variable: gtag/,
    /^Can't find variable: ga/,
    /^google is not defined/,

    // Ad-related errors
    // NOTE: 'blocked by client' 已移到 beforeSend 的 NETWORK_NOISE —— 拦截插件挡掉支付 SDK
    // 时也是这条消息,那不是我们的 bug 但是真实的支付故障,得先过支付白名单
    'AdblockPlus',
    /adnxs\.com/,
    /doubleclick\.net/,
    /googlesyndication/,
    /googletagmanager/,

    // Browser extension errors
    'chrome-extension',
    'moz-extension',
    'safari-extension',
    'safari-web-extension',
  ],
})
